import { css } from '@emotion/core';
import SearchBox from '@veeva/corgix/searchbox';
import { ThemeProvider, useTheme } from 'emotion-theming';
import { useContext, useEffect, useState } from 'react';
import ResponseStatus from '../../constants/ResponseStatus';
import AuthenticatedRouteLanguageContext from '../../services/AuthenticatedRouteLanguageContext';
import { showErrorNotificationFromResponse } from '../../services/errorUtils';
import useMessageService from '../../services/i18n/useMessageService';
import { fetchHomeApplications } from '../../veevaIdApi';
import ApplicationGrid from './ApplicationGrid';
import getTargetAttribute from '../../services/getTargetAttribute';

const theme = {
    linkFontSize: '1rem',
    textInputHeight: '2.3333rem',
    textInputFontSize: '1rem',
    textInputBorderRadius: '2px',
    textInputSpacing: '0',
    textInputSpacingVariant1: '0.6666rem',
};

async function fetchApplications(language) {
    const response = await fetchHomeApplications();
    if (response.responseStatus !== ResponseStatus.SUCCESS) {
        const i18nOptions = { language };
        showErrorNotificationFromResponse(response, i18nOptions);
        return [];
    }
    const transformed = response.data.map((app) => {
        return { ...app, lastLogin: new Date(app.lastLogin) };
    });
    return transformed;
}

export default function HomeContent() {
    const { grayLighter } = useTheme();
    const { messageLanguage } = useContext(AuthenticatedRouteLanguageContext);
    const [[myApplications, searchPlaceholder, noApplicationsTitle, noApplicationsSubtitle]] =
        useMessageService([
            'home.my_applications',
            'general.search',
            'home.no_applications',
            'home.no_applications_subtitle',
        ]);

    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [applications, setApplications] = useState([]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const res = await fetchApplications(messageLanguage);
            setApplications(res);
            setLoading(false);
        })();
    }, [messageLanguage]);

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const title = (
        <h1
            css={css`
                margin: 0 0 2rem 0;
                font-size: 2rem;
                font-weight: normal;
            `}
        >
            {myApplications}
        </h1>
    );

    if (loading) {
        return title;
    }

    if (applications.length === 0) {
        return (
            <>
                <div
                    css={css`
                        position: absolute;
                    `}
                >
                    {title}
                </div>
                <div
                    css={css`
                        flex-grow: 1;
                        text-align: center;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    `}
                >
                    <h2
                        css={css`
                            font-size: 2rem;
                            margin: 0 0 1.3333rem 0;
                        `}
                        data-target={getTargetAttribute('home-no-applications-title')}
                    >
                        {noApplicationsTitle}
                    </h2>
                    <h3
                        css={css`
                            font-size: 1.25rem;
                            font-weight: normal;
                            color: ${grayLighter};
                            margin: 0;
                        `}
                        data-target={getTargetAttribute('home-no-applications-subtitle')}
                    >
                        {noApplicationsSubtitle}
                    </h3>
                </div>
            </>
        );
    }

    return (
        <>
            {title}
            <ThemeProvider theme={theme}>
                <div
                    css={css`
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 0.6666rem;
                    `}
                >
                    <SearchBox
                        value={searchTerm}
                        onChange={(e, value) => {
                            handleSearch(value);
                        }}
                        onSearch={handleSearch}
                        onClear={() => {
                            handleSearch('');
                        }}
                        placeholder={searchPlaceholder}
                        size="lg"
                    />
                </div>
                <ApplicationGrid
                    loading={loading}
                    applications={applications}
                    searchTerm={searchTerm}
                />
            </ThemeProvider>
        </>
    );
}
